import { useState } from "react";
import { dollars, ready, takeoverLine, takeoverSteps, takeoverWhy } from "../../src/takeover-words.ts";
import { ApiError, api, type TripView } from "./api.ts";
import { firstName } from "./faces.ts";
import { localToday, weekday } from "./format.ts";
import { ReportThis } from "./ReportThis.tsx";
import { Avatar, Button } from "./souvenir.ts";
import { ButtonLink } from "./ui.tsx";

// A trip offered to me by its owner (DESIGN.md, Member, "Taking over a trip"; features/trips-and-members.md): it waits
// on me at the top of People, saying what taking it over needs now (src/takeover-words.ts), with Turn down and Take
// over. When something is to be bought first, Take over says its price and goes through Checkout (/buy/takeover), a
// Trip Pass first and then the spots, each coming back here; the trip is mine once what it needs is paid. On a
// read-only trip it can't change hands: only Turn down.

// "Sat 15 Nov": the last day the offer waits, on this device's calendar.
export const offerUntil = (until: string) => weekday(localToday(new Date(until)));

export function OfferToYou({ trip, onChanged }: { trip: TripView; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const offer = trip.offer;
  if (!offer || offer.to !== trip.you) return null;
  const by = trip.members.find((m) => m.id === offer.by);
  const name = by ? firstName(by.name) : "Its owner";
  const needs = offer.needs ?? null;
  const locked = Boolean(trip.locked) || !needs;
  const back = `/t/${trip.id}?view=people`;
  const buy = `/buy/takeover?${new URLSearchParams({ trip: trip.id, from: "web", return_to: back })}`;
  const act = async (run: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await run();
      onChanged();
    } catch (e) {
      // Taking it over needs more than it did when the page loaded (someone let in meanwhile, a pass used): the
      // page loads it again, saying what it needs now.
      if (e instanceof ApiError && e.status === 402) onChanged();
      setError((e as Error).message.replace(/^operation \d+: /, ""));
    } finally {
      setBusy(false);
    }
  };
  const why = needs ? takeoverWhy(needs) : null;
  const steps = needs ? takeoverSteps(needs) : null;
  const both = steps !== null;
  return (
    <section className="tm-people-block" aria-labelledby="offered">
      <h2 id="offered" className="title">
        Offered to you
      </h2>
      <article className="tm-offer" aria-label={`${name} offers you ${trip.title}`}>
        <div className="tm-offer-who">
          <Avatar
            name={by?.name ?? name}
            color={by?.color ?? "amber"}
            {...(by?.src ? { src: by.src } : {})}
            size={40}
          />
          <span className="tm-person-name">
            <span className="body-strong">
              {name} offers you {trip.title}
            </span>
            <span className="caption tm-muted">
              You'd be its owner and decide who gets in. Until {offerUntil(offer.until)}.
            </span>
          </span>
        </div>
        {locked ? (
          <p className="callout">The trip is read-only, so it can't change hands for now.</p>
        ) : (
          <div className="tm-offer-needs">
            <p className="callout">{takeoverLine(needs)}</p>
            {why && <p className="caption tm-muted">{why}</p>}
            {steps && <p className="caption tm-muted">{steps}</p>}
          </div>
        )}
        <div className="tm-offer-actions">
          <Button variant="quiet" size="sm" disabled={busy} onClick={() => act(() => api.declineOffer(trip.id))}>
            Turn down
          </Button>
          {!locked &&
            (ready(needs) ? (
              <Button variant="primary" size="sm" disabled={busy} onClick={() => act(() => api.takeOver(trip.id))}>
                Take over
              </Button>
            ) : (
              <ButtonLink variant="primary" size="sm" href={buy}>
                {both ? `Get the Trip Pass · ${dollars(needs.prices.pass)}` : `Take over · ${dollars(needs.cost)}`}
              </ButtonLink>
            ))}
        </div>
        {error && (
          <p className="callout tm-error" role="alert">
            {error}
            <ReportThis />
          </p>
        )}
      </article>
    </section>
  );
}
